import {
  GemSpec,
  BaseProcess,
  BaseProcessMetadata,
  CommonGraph,
  CommonState,
  Connection,
  GenericGraphProcessType,
  GenericGraphProcesses,
  Process
} from '@common/types';
import { PropertiesDidSave } from '@LSP/base/hook';
import { useFitView } from '@prophecy/ui-v3/Graph/hooks';
import { ADD_NEW_PORT_HANDLE_ID, NODE_DIMENSION } from '@prophecy/ui-v3/Graph/tokens';
import { EdgeData, NodeData } from '@prophecy/ui-v3/Graph/types';
import { doubleId } from '@prophecy/utils/id';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { uniqueID } from '@prophecy/utils/uniqueID';
import { nanoid } from 'nanoid';
import React, { useEffect, useState } from 'react';
import { Edge, Connection as EdgeConnection, getRectOfNodes, useStoreApi, XYPosition, Node } from 'reactflow';
import { Store } from 'redux';

import { findBindings, isSubGraphOrMetaGem, resolveBindingPath, resolveBindingValue } from '../../Parser/bindings';
import { Field, RecordType } from '../../schema/configTypes';
import { spaceToUnderscore, uniqueIDWithDelimiter } from '../../utils';
import { SUB_GRAPH_NODE_TYPES } from '../constants';
import { getGemSpec } from '../getGemSpec';
import { getGemTypeByProcess } from '../getGemTypeByProcess';
import { useAddProcessesAndConnections } from '../useAddProcessesAndConnections';
import { getAllProcessLabels, getGraphToExtractProcessLabels } from '../utils/graph/process';
import { getPosition } from './processMetadataPosition';
import { isReusableSubgraph } from './utils';

export function isCyclicGraph(
  edges: Pick<Edge, 'source' | 'target'>[],
  target: string,
  currentNode: string,
  visited = new Set<string>()
): boolean {
  if (target === currentNode) return true;
  if (visited.has(currentNode)) return false; // prevent infinite loops in cycles

  visited.add(currentNode);

  const incomingConnections = edges.filter((edge) => edge.target === currentNode);

  for (const conn of incomingConnections) {
    if (isCyclicGraph(edges, target, conn.source, visited)) {
      return true;
    }
  }

  return false;
}

export function findCyclicNodes(
  edges: Pick<Edge, 'source' | 'target'>[],
  targets: Record<string, boolean>,
  currentNode: string,
  cyclicNodes: Record<string, boolean>,
  visited: Set<string> = new Set()
): void {
  // If we've already visited this node, skip to prevent infinite loops
  if (visited.has(currentNode)) {
    return;
  }

  visited.add(currentNode);

  if (targets[currentNode]) {
    cyclicNodes[currentNode] = true;
  }

  // find outgoing connections and traverse each target
  const outgoingConnections = edges.filter((edge) => edge.source === currentNode);
  for (const conn of outgoingConnections) {
    findCyclicNodes(edges, targets, conn.target, cyclicNodes, visited);
  }
}

function updateIdsInProperties(node: BaseProcess<BaseProcessMetadata>, oldNewIdsMap: Array<[string, string]>) {
  let nodePropsString = JSON.stringify(node.properties);
  oldNewIdsMap.forEach(([oldId, newId]) => {
    // this is to handle cases where portIds are like out0, out1 (for old workflows).
    if (oldId.length >= 21) {
      // we are not using string.replaceAll because
      // our ids have $ and it is treated as special character
      nodePropsString = nodePropsString.split(oldId).join(newId);
    }
  });
  node.properties = JSON.parse(nodePropsString);
}

export function prepareNodesForPaste<G extends CommonGraph>({
  specs,
  nodes,
  labels,
  idMap,
  portIdMap,
  reusableSubgraph,
  pasteOffset = { x: 0, y: 0 },
  subgraphLabels = {},
  getSubgraphSpec,
  prepareUnitTestForPaste,
  prepareSubgraphConfigurationForPaste
}: {
  specs: GemSpec[];
  nodes: GenericGraphProcessType<G>[];
  labels: string[];
  idMap: { [key: string]: string };
  portIdMap: { [key: string]: string };
  reusableSubgraph?: boolean;
  pasteOffset?: { x: number; y: number };
  subgraphLabels?: { [key: string]: string };
  getSubgraphSpec?: (node: GenericGraphProcessType<G>) => void;
  prepareUnitTestForPaste?: (
    node: GenericGraphProcessType<G>,
    portIdMap: { [key: string]: string },
    oldNodeId: string
  ) => void;
  prepareSubgraphConfigurationForPaste?: (node: GenericGraphProcessType<G>, labels?: { [key: string]: string }) => void;
}) {
  nodes.forEach((node) => {
    const oldLabel = node.metadata.label;
    if (isSubGraphOrMetaGem(node, specs)) {
      // if the node or the parent is reusable subgraph all the child node be treated to be part of it.
      reusableSubgraph = reusableSubgraph || isReusableSubgraph(node);
    }

    if (labels.includes(oldLabel)) {
      const label = uniqueID(`${oldLabel}_`, labels);
      node.metadata.slug = spaceToUnderscore(label);
      node.metadata.label = label;
      labels.push(label);
      if (isSubGraphOrMetaGem(node, specs)) {
        subgraphLabels[oldLabel] = label;
      }
    }
    const oldId = node.id;
    const newId = doubleId(reusableSubgraph ? oldId : undefined);
    idMap[oldId] = newId;
    node.id = newId;

    // only update node position if it is not a inner nodes of a visual group
    if (!node.parent) {
      const base = getPosition(node.metadata);
      const x = base.x + pasteOffset.x;
      const y = base.y + pasteOffset.y;
      node.metadata.x = x;
      node.metadata.y = y;
    }

    const oldNewPortIdsMap: Array<[string, string]> = [];

    node.ports?.inputs?.forEach((port) => {
      const newId = doubleId(reusableSubgraph ? port.id : undefined);
      portIdMap[getLookupKey(oldId, port.id)] = newId;
      oldNewPortIdsMap.push([port.id, newId]);
      port.id = newId;
    });
    node.ports?.outputs?.forEach((port) => {
      const newId = doubleId(reusableSubgraph ? port.id : undefined);
      portIdMap[getLookupKey(oldId, port.id)] = newId;
      oldNewPortIdsMap.push([port.id, newId]);
      port.id = newId;
    });

    updateIdsInProperties(node, oldNewPortIdsMap);

    prepareUnitTestForPaste?.(node, portIdMap, oldId);
    if (isSubGraphOrMetaGem(node, specs)) {
      const subgraphNodes = Object.values(node.processes) as GenericGraphProcessType<G>[];
      prepareNodesForPaste({
        specs,
        nodes: subgraphNodes,
        labels,
        idMap,
        portIdMap,
        reusableSubgraph,
        pasteOffset,
        subgraphLabels,
        getSubgraphSpec,
        prepareUnitTestForPaste
      });
      prepareSubgraphConfigurationForPaste?.(node, subgraphLabels);

      node.processes = {};
      subgraphNodes.forEach((subgraphNode) => {
        node.processes[subgraphNode.id] = subgraphNode;
      });
      node.connections = node.connections.map((conn) => {
        const id = nanoid();
        const source = idMap[conn.source];
        const sourcePort = portIdMap[getLookupKey(conn.source, conn.sourcePort)];
        const target = idMap[conn.target];
        const targetPort = portIdMap[getLookupKey(conn.target, conn.targetPort)];

        return {
          id,
          source,
          sourcePort,
          target,
          targetPort
        };
      });
    }
  });
  // update parent id for inner nodes of a visual group
  for (const node of nodes) {
    if (node.parent) {
      const newId = idMap[node.parent];
      node.parent = newId;
    }
  }
}

export function getLookupKey(processId: string, portId: string) {
  return `${processId}-${portId}`;
}

export function validateHandle(
  edges: Edge[],
  processes: GenericGraphProcesses<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, Connection> = {},
  ignorePorts: string[] = []
) {
  return (connection: EdgeConnection) => {
    if (connection.target && connection.source) {
      if (
        ignorePorts.includes(connection.targetHandle as string) ||
        ignorePorts.includes(connection.sourceHandle as string)
      ) {
        return false;
      }
      const sourceIsInputPort =
        connection.source.startsWith(SUB_GRAPH_NODE_TYPES.subGraphOutNode) ||
        !!processes[connection.source]?.ports?.inputs?.find((port) => port.id === connection.sourceHandle);
      // ADD_NEW_PORT_HANDLE_ID is not part of port list as it is virtual Port
      const targetIsInputPort =
        connection.target.startsWith(SUB_GRAPH_NODE_TYPES.subGraphOutNode) ||
        !!processes[connection.target]?.ports?.inputs?.find((port) => port.id === connection.targetHandle) ||
        connection.targetHandle === ADD_NEW_PORT_HANDLE_ID;
      const sourceIsOutputPort =
        connection.source.startsWith(SUB_GRAPH_NODE_TYPES.subGraphInNode) ||
        !!processes[connection.source]?.ports?.outputs?.find((port) => port.id === connection.sourceHandle);
      const targetIsOutputPort =
        connection.target.startsWith(SUB_GRAPH_NODE_TYPES.subGraphInNode) ||
        !!processes[connection.target]?.ports?.outputs?.find((port) => port.id === connection.targetHandle);

      if (sourceIsInputPort && targetIsInputPort) return false;
      if (sourceIsOutputPort && targetIsOutputPort) return false;

      const existingConnection = edges.find((edge) => {
        return edge.targetHandle === connection.targetHandle && edge.target === connection.target;
      });
      if (existingConnection) {
        return false;
      } else {
        return !isCyclicGraph(edges, connection.target, connection.source);
      }
    }
    return true;
  };
}

export function useValidateHandle(
  edges: Edge[],
  processes: GenericGraphProcesses<unknown, BaseProcessMetadata, BaseProcess<BaseProcessMetadata>, Connection> = {},
  ignorePorts: string[] = []
): ReturnType<typeof validateHandle> {
  return usePersistentCallback((connection: EdgeConnection) => {
    return validateHandle(edges, processes, ignorePorts)(connection);
  });
}

function generateSelectionMap<N = NodeData, E = EdgeData>(items: (Node<N> | Edge<E>)[]) {
  const selectionMap = new Map<string, boolean>();
  items.forEach((item) => {
    if (item.selected) {
      selectionMap.set(item.id, true);
    }
  });
  return selectionMap;
}

/**
 * apply existing properties and position to the dragging nodes
 */
function applyExistingProperties<N = NodeData>(nodes: Node<N>[], existingNodes: Node<N>[]) {
  const existingNodeMap = new Map<string, Node<N>>();
  existingNodes.forEach((node) => {
    existingNodeMap.set(node.id, node);
  });
  return nodes.map((node) => {
    const existingNode = existingNodeMap.get(node.id);

    // for dragging node we want to always use the dragging position
    const position = existingNode?.dragging ? existingNode.position : node.position;
    const positionAbsolute = existingNode?.dragging ? existingNode.positionAbsolute : node.positionAbsolute;

    return { ...existingNode, ...node, position, positionAbsolute };
  });
}

function mergeSelectionState<N = NodeData, E = EdgeData>(
  items: (Node<N> | Edge<E>)[],
  selectionMap: Map<string, boolean>
) {
  return items.map((item) => {
    const selected = selectionMap.get(item.id);
    if (selected) {
      return { ...item, selected };
    }
    return item;
  });
}

function generateVirtualEdges<E = EdgeData>(existingEdges: Edge<E>[], edges: Edge<E>[]) {
  const virtualEdgesMap = existingEdges.reduce((map, item) => {
    if ((item.data as unknown as EdgeData)?.virtual) {
      map.set(`${item.source},${item.sourceHandle},${item.target},${item.targetHandle}`, item);
    }
    return map;
  }, new Map<string, Edge<E>>());

  edges.forEach((item) => {
    virtualEdgesMap.delete(`${item.source},${item.sourceHandle},${item.target},${item.targetHandle}`);
  });
  const virtualEdgesToAdd = virtualEdgesMap.values();
  return virtualEdgesToAdd;
}

function generateVirtualNodes<N = NodeData>(existingNodes: Node<N>[], nodes: Node<N>[]) {
  const virtualNodesMap = existingNodes.reduce((map, item) => {
    if ((item.data as unknown as NodeData)?.virtual) {
      map.set(item.id, item);
    }
    return map;
  }, new Map<string, Node<N>>());
  nodes.forEach((item) => {
    virtualNodesMap.delete(item.id);
  });
  const virtualNodesToAdd = virtualNodesMap.values();
  return virtualNodesToAdd;
}

export function useGraphUpdater<N = NodeData, E = EdgeData>(
  nodes: Node<N>[],
  edges: Edge<E>[],
  setNodes: React.Dispatch<React.SetStateAction<Node<N>[]>>,
  setEdges: React.Dispatch<React.SetStateAction<Edge<E>[]>>
) {
  // const reactFlowInstanceRef = useReactFlowInstance<N, E>();
  /**
   * We avoid rendering react flow graph until the nodes are set,
   * otherwise the auto layout and fit to view will happen on empty nodes
   * and would trigger again. So we maintain the initialized flag
   */
  const [initialized, setInitialized] = useState(false);

  // update graph nodes and edges in next render cycle
  useEffect(() => {
    // persevering selection as newly generated nodes/edges object won't have selection information

    setNodes((existingNodes: Node<N>[]) => {
      /**
       * keep the position from existing graph for the dragging nodes,  so if the node is being dragged, while
       * graph node updates, it doesn't reset the position
       * Also, copy all other properties which react flow sets on the node, from existing nodes
       * like selected
       */
      const _nodes = applyExistingProperties(nodes, existingNodes);
      const virtualNodesToAdd = generateVirtualNodes(existingNodes, nodes);
      return [..._nodes, ...virtualNodesToAdd] as Node<N>[];
    });
    setEdges((existingEdges: Edge<E>[]) => {
      const virtualEdgesToAdd = generateVirtualEdges(existingEdges, edges);
      const selectedEdgesMap = generateSelectionMap(existingEdges);
      return [...mergeSelectionState(edges, selectedEdgesMap), ...virtualEdgesToAdd] as Edge<E>[];
    });

    // if not already initialized mark graph as initialized
    if (!initialized) setInitialized(true);
  }, [nodes, edges, initialized, setNodes, setEdges]);

  return initialized;
}

export async function applyPasteChanges<G extends CommonGraph>(
  store: ReturnType<typeof useStoreApi>,
  connections: { id: string; source: string; sourcePort: string; target: string; targetPort: string }[],
  processesToAdd: GenericGraphProcessType<G>[],
  addProcessesAndConnections: ReturnType<typeof useAddProcessesAndConnections>,
  propertiesDidSave: PropertiesDidSave
) {
  addProcessesAndConnections(processesToAdd, connections);
  propertiesDidSave();

  const { addSelectedEdges, addSelectedNodes, resetSelectedElements } = store.getState();
  resetSelectedElements();
  store.setState({ multiSelectionActive: true, nodesSelectionActive: true });
  addSelectedEdges(connections.map((connection) => connection.id));
  addSelectedNodes(processesToAdd.map((process) => process.id));
  store.setState({ multiSelectionActive: false });
  // fire blur event to force reactflow's useKeyPressed to flush out(reset) local state
  window.dispatchEvent(new FocusEvent('blur'));
}

export function processToBasicNode<G extends CommonGraph>(process: GenericGraphProcessType<G>): Node<unknown> {
  return {
    id: process.id,
    type: 'gem',
    position: getPosition(process.metadata),
    width: process.metadata.width ?? NODE_DIMENSION,
    height: process.metadata.height ?? NODE_DIMENSION,
    parentNode: process.parent,
    data: {}
  };
}

export function getRectOfProcesses<G extends CommonGraph>(processesToAdd: GenericGraphProcessType<G>[]) {
  return getRectOfNodes(processesToAdd.filter((p) => !p.parent).map(processToBasicNode));
}

export function getPasteOffset<G extends CommonGraph>(processesToAdd: GenericGraphProcessType<G>[], point: XYPosition) {
  // bbox for nodes
  const { x, y, width, height } = getRectOfProcesses(processesToAdd);
  // center of bbox
  const cx = width / 2 + x;
  const cy = height / 2 + y;
  return { x: point.x - cx, y: point.y - cy };
}

export function getSubgraphConfig(commonConfigFields: Field[], subgraphName: string): Field[] {
  if (!commonConfigFields.length) {
    return [];
  }

  let configField: Field | undefined;

  const findSubgraph = (fields: Field[]): Field | undefined => {
    return fields.find((item) => {
      if (item.name === subgraphName) {
        configField = item;
        return true;
      }

      if ((item?.kind as RecordType)?.fields) {
        const subgraphConfig = findSubgraph((item?.kind as RecordType)?.fields);
        if (subgraphConfig) {
          configField = subgraphConfig;
          return true;
        }
      }

      return false;
    });
  };

  findSubgraph(commonConfigFields);

  return ((configField?.kind as RecordType)?.fields || []).filter(
    (config: Field) => !Boolean((config?.kind as RecordType)?.fields || !config?.name)
  );
}

export function updateJoinNodeLabel(
  joinProcess: BaseProcess<BaseProcessMetadata>,
  source1Label: string,
  source2Label: string,
  rootGraph: CommonGraph,
  graph: CommonGraph,
  joinColumns?: string[]
) {
  let label: string;
  if (joinColumns) {
    label = `by_${joinColumns.slice(0, 2).join('_')}`;
  } else {
    label = `${source1Label}_${source2Label}`;
  }

  label = uniqueIDWithDelimiter(label, getAllProcessLabels(getGraphToExtractProcessLabels(rootGraph, graph), []));

  joinProcess.metadata.label = label;
  joinProcess.metadata.slug = spaceToUnderscore(label);
}

export function updateLabelOnSuggestedJoinNode(
  process: BaseProcess<BaseProcessMetadata>,
  connections: Connection[],
  rootGraph: CommonGraph,
  graph: CommonGraph
) {
  // if new node is getting added to upstream
  const upstreamProcess = connections
    .filter((connection) => connection.target === process.id)
    .map((connection) => graph.processes[connection.source]);

  if (upstreamProcess.length === 2) {
    updateJoinNodeLabel(
      process,
      upstreamProcess[0].metadata.label,
      upstreamProcess[1].metadata.label,
      rootGraph,
      graph
    );
  }
}

export function getProcessAltLabel({
  store,
  process
}: {
  store: Store<CommonState>;
  process: BaseProcess<BaseProcessMetadata>;
}) {
  const { id: processId } = process;
  const state = store.getState();
  const { root, graphPath, gemSpecs } = state;
  const currentGemSpec = getGemSpec(gemSpecs, getGemTypeByProcess(process));
  if (!currentGemSpec?.altProcessLabel) return;

  const [bindings, staticValue] = findBindings({ altProcessLabel: currentGemSpec.altProcessLabel });
  // if there is no binding return as it
  if (staticValue.altProcessLabel) return staticValue.altProcessLabel;

  const altLabelPropertyPath = resolveBindingPath(bindings.altProcessLabel, graphPath, processId);

  return resolveBindingValue(state, altLabelPropertyPath, root);
}

/**
 * Collects all the upstream processes from the given process id using depth first search.
 * @param graph - The graph to get the upstream processes from
 * @param processId - The process id to get the upstream processes from
 * @param options - The options to get the upstream processes from
 * @param options.level - The level of the upstream processes to get
 * @param options.excludeParentGraphProcess - Whether to exclude the parent graph process
 * @returns The upstream processes
 */
export function getUpstreamProcesses(
  graph: CommonGraph,
  processId: string,
  options?: {
    level?: 'direct' | 'deep';
    excludeParentGraphProcess?: boolean;
  }
) {
  const processes: Process[] = [];
  if (!graph.connections) {
    return processes;
  }

  const excludeParent = Boolean(options?.excludeParentGraphProcess);
  const result: Process[] = [];
  const stack: string[] = [processId];
  const visited: Set<string> = new Set();

  const isLevelDeep = options?.level === 'deep';

  const incomingByTarget = new Map<string, string[]>();

  // Build a map of incoming connections (sources) by target process id
  for (const conn of graph.connections) {
    const sources = incomingByTarget.get(conn.target);
    if (sources) {
      sources.push(conn.source);
    } else {
      incomingByTarget.set(conn.target, [conn.source]);
    }
  }

  while (stack.length) {
    const current = stack.pop();
    if (!current) continue;

    if (isLevelDeep) {
      if (visited.has(current)) continue;

      visited.add(current);
    }

    const sources = incomingByTarget.get(current);
    if (!sources) continue;

    for (const source of sources) {
      const upstreamProcess = graph.processes[source];

      if (!upstreamProcess) {
        continue;
      }
      // Exclude all the subgraphs process when excludeParentGraphProcess is true as we don't want to traverse the subgraphs inside the parent graph
      if (excludeParent && graph.id === processId) {
        continue;
      }

      result.push(upstreamProcess);
      if (isLevelDeep) {
        stack.push(upstreamProcess.id);
      }
    }
  }
  return result;
}

export function getDownstreamProcesses(
  graph: CommonGraph,
  processId: string,
  options?: {
    level?: 'direct' | 'deep';
    excludeParentGraphProcess?: boolean;
  }
) {
  const processes: Process[] = [];
  if (!graph.connections) {
    return processes;
  }
  const connections = graph.connections.filter((conn) => conn.source === processId);
  connections.forEach((conn) => {
    const process = graph.processes[conn.target];

    if (options?.excludeParentGraphProcess && graph.id === processId) {
      return;
    }

    processes.push(process);
    if (options?.level === 'deep') {
      processes.push(...getDownstreamProcesses(graph, process.id, options));
    }
  });

  return processes;
}

export function useFitProcessesInViewPort() {
  const fitView = useFitView();

  const fitProcessesInViewPort = usePersistentCallback((processes: CommonGraph['processes']) => {
    const nodes = Object.values(processes).map(processToBasicNode);

    if (!nodes.length) {
      return;
    }

    fitView(nodes);
  });

  return fitProcessesInViewPort;
}

/**
 * Deduplicate interim icons for multiport gems: when multiple edges connect
 * the same source-target pair, their interim icons overlap.
 * since they show identical data. This keeps only the last interim per source-target pair.
 */
export function deduplicateEdgeInterims(edges: Edge<EdgeData>[]) {
  // Iterate backwards: so the first edge we encounter per pair is the last one
  // last edge have highest z-index, so we keep it and clear all earlier duplicates.
  const seenPairs = new Set<string>();
  for (let i = edges.length - 1; i >= 0; i--) {
    const edge = edges[i];
    if (edge.data?.points?.length) {
      // we included sourceLabel to handle cases where gems have multiple ouputs ports(Ex. SQLStatement gem in spark).
      const pairKey = `${edge.source}-${edge.data.sourceLabel}<->${edge.target}`;
      if (seenPairs.has(pairKey)) {
        edge.data.points = [];
      } else {
        seenPairs.add(pairKey);
      }
    }
  }
}
